import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import HalfLayout from "components/HalfLayout";
import Map from "components/Map";
import Placeholder from "components/Placeholder";
import IndicatorsActioner from "services/indicators/actioner";
import IndicatorsUtils from "services/indicators/utils";
import IndicatorsLayout from "./components/IndicatorsLayout";
import MapComponent from "./components/MapComponent";

function Indicators({
  retrieveIndicators,
  boSettingsIndicators,
  indicators,
  from,
  to,
  initialized,
  refreshIndicators,
}) {
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          boSettingsIndicators
            .map((boIndicator) => {
              return Object.values(
                boIndicator.items.map((item) => Object.keys(item))
              );
            })
            .flat(2)
            .filter((item) => item !== "subtitle")
        )
      ),
    [boSettingsIndicators]
  );

  const [autoSelectIndicatorCells, setAutoSelectIndicatorCells] =
    useState(true);
  const [fullscreenTable, setFullscreenTable] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(
    Object.entries(IndicatorsUtils.INDICATORS_COLUMNS).find(
      ([key, value]) => value === categories[0]
    )?.[0] ?? IndicatorsUtils.INDICATORS_TYPES.CARS
  );

  const [selectedIndicators, setSelectedIndicators] = useState(
    [...Array(boSettingsIndicators.length).keys()].map(() => ({
      checked: false,
      areasChecked: [],
    }))
  );
  const [hoveredIndicator, setHoveredIndicator] = useState(null);
  const [leafletMap, setLeafletMap] = useState();

  const selectEveryIndicatorsAreas = useCallback(() => {
    const selectedIndicatorsCopy = structuredClone(selectedIndicators);

    indicators[0].items.forEach((_, index) => {
      selectedIndicatorsCopy[0].areasChecked[index] = {
        index: index,
        uuid: crypto.randomUUID(),
      };
    });

    setSelectedIndicators(selectedIndicatorsCopy);
  }, [indicators, selectedIndicators]);

  const selectEveryIndicatorsContracts = useCallback(() => {
    const selectedIndicatorsCopy = structuredClone(selectedIndicators);
    selectedIndicatorsCopy.map((selectedIndicator, index) => {
      selectedIndicator.checked = true;
      selectedIndicator.uuid = crypto.randomUUID();

      return selectedIndicator;
    });

    setSelectedIndicators(selectedIndicatorsCopy);
  }, [selectedIndicators]);

  useEffect(() => {
    setSelectedIndicators(
      [...Array(boSettingsIndicators.length).keys()].map(() => ({
        checked: false,
        areasChecked: [],
      }))
    );
    setAutoSelectIndicatorCells(true);
  }, [boSettingsIndicators]);

  useEffect(() => {
    if (!initialized) {
      retrieveIndicators();
    }
  }, [
    boSettingsIndicators,
    from,
    indicators.length,
    initialized,
    retrieveIndicators,
    selectEveryIndicatorsAreas,
    selectEveryIndicatorsContracts,
    to,
  ]);

  useEffect(() => {
    if (initialized && autoSelectIndicatorCells) {
      setSelectedColumn(
        Object.entries(IndicatorsUtils.INDICATORS_COLUMNS).find(
          ([key, value]) => value === categories[0]
        )?.[0] ?? IndicatorsUtils.INDICATORS_TYPES.CARS
      );

      if (indicators.length === 1) {
        selectEveryIndicatorsAreas();
        return setAutoSelectIndicatorCells(false);
      }

      if (indicators.length > 1) {
        selectEveryIndicatorsContracts();
        return setAutoSelectIndicatorCells(false);
      }
    }
  }, [
    autoSelectIndicatorCells,
    categories,
    indicators.length,
    initialized,
    selectEveryIndicatorsAreas,
    selectEveryIndicatorsContracts,
  ]);

  const handleChangeTableFullscreen = () => {
    setFullscreenTable(!fullscreenTable);
  };

  return (
    <HalfLayout
      fullHalf
      fullscreen={fullscreenTable}
      leftComponent={
        <IndicatorsLayout
          retrieveIndicators={() => {
            setAutoSelectIndicatorCells(false);
            refreshIndicators();
          }}
          indicators={indicators}
          tableFullScreen={fullscreenTable}
          onChangeTableFullScreen={handleChangeTableFullscreen}
          selectedColumn={selectedColumn}
          setSelectedColumn={setSelectedColumn}
          selectedIndicators={selectedIndicators}
          setSelectedIndicators={setSelectedIndicators}
          hoveredIndicator={hoveredIndicator}
          setHoveredIndicator={setHoveredIndicator}
          leafletMap={leafletMap}
          categories={categories}
        />
      }
      rightComponent={
        <Map
          recenterButton={false}
          withCheckboxOptions={{
            aisles: false,
            labels: false,
            displays: false,
            spaceLabels: false,
          }}
          mapComponent={
            !initialized ? (
              <Placeholder delay={500} height="100%" />
            ) : (
              <MapComponent
                indicators={indicators}
                selectedColumn={selectedColumn}
                selectedIndicators={selectedIndicators}
                hoveredIndicator={hoveredIndicator}
                leafletMap={leafletMap}
                setLeafletMap={setLeafletMap}
              />
            )
          }
        />
      }
    />
  );
}

const mapStateToProps = (state) => ({
  boSettingsIndicators: state.Indicators.indicators,
  indicators: state.Indicators.data,
  from: state.Indicators.filters.from,
  to: state.Indicators.filters.to,
  initialized: state.Indicators.initialized,
});

const mapDispatchToProps = (dispatch) => ({
  retrieveIndicators: () => dispatch(IndicatorsActioner.retrieveIndicators()),
  refreshIndicators: () => dispatch(IndicatorsActioner.setInitialized(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Indicators);
